import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ActivityForm from "./_form";
import { FormValues } from "./_form/validation/schema";
import MultiStepFormSkeleton from "@/components/Skeleton/MultiStepFormSkeleton";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_ACTIVITY_MUTATION } from "./_form/queries";
import { GET_ACTIVITY_TYPE } from "../queries";
import { Navigate } from "react-router-dom";
import { CreatesEventType, RateUnit } from "../types";

export default function CreateActivityPage() {
  const navigate = useNavigate();
  const { activityTypeId = "" } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const { data: activityTypeData, loading: activityTypeLoading } = useQuery(GET_ACTIVITY_TYPE, {
    variables: { id: activityTypeId },
    skip: !activityTypeId
  });

  const [createActivityMutation] = useMutation(CREATE_ACTIVITY_MUTATION);

  if (activityTypeData?.activityType?.createsEvent === CreatesEventType.ALWAYS) {
    return <Navigate to={"/activities/events/new?activityTypeId=" + activityTypeData?.activityType?.id} replace />;
  }

  if (activityTypeLoading) {
    return <MultiStepFormSkeleton stepCount={3} renderStepper={true} formSections={3} />;
  }

  if (!activityTypeData) {
    return null;
  }

  const rateUnit = activityTypeData?.activityType?.config?.creditRates?.[0]?.rateUnit;

  const handleSubmit = async (data: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorBanner(null);
    try {
      const { data: responseData } = await createActivityMutation({
        variables: {
          input: {
            title: data.title,
            activityType: activityTypeData?.activityType?.title ?? data.activityType,
            description: data.activityDescription,
            managingSectionId: data.sectionId,
            duration: data.duration ? parseFloat(data.duration) : null,
            ...(data.startDate?.startDate && { allowParticipationStarting: data.startDate?.startDate }),
            ...(data.endDate?.startDate && { allowParticipationThrough: data.endDate?.startDate }),
            checkinEnabled: data.checkinEnabled
          }
        }
      });
      const { success, activity, message } = responseData.createActivity;
      if (success) {
        navigate(`/activities/activity-types/${activityTypeId}/activity/${activity.id}`);
        showSnackbar("Activity created");
      } else {
        const errorMessage = message || "Failed to create activity";
        setErrorBanner(errorMessage);
        showErrorSnackbar(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to create activity";
      setErrorBanner(errorMessage);
      showErrorSnackbar(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ActivityForm
      onSubmit={handleSubmit}
      initialValues={rateUnit === RateUnit.INSTANCE ? { duration: "1" } : undefined}
      isSubmitting={isSubmitting}
      errorBanner={errorBanner ?? undefined}
      setErrorBanner={(banner) => setErrorBanner(banner ?? null)}
      rateUnit={rateUnit}
    />
  );
}
